/**
 * Removing a survey, in the surveyor's words (DECISIONS D-012).
 *
 * Three outcomes, three different things to say, and the words are the feature
 * as much as the code is. The person tapping is deciding between:
 *
 *   "this is gone from this phone"      — nothing else ever knew about it
 *   "this is withdrawn from the office" — something else does, and is being told
 *   "this is not yours to remove"       — it has already been submitted
 *
 * A single "Are you sure?" would collapse all three into a shrug, which is how
 * the wrong survey gets deleted. So every string here NAMES the survey and says
 * what happens to it, and the three paths never share a sentence.
 *
 * Pure, and separate from the component for the same reason the rest of
 * src/ui/state is: the copy is the part worth testing, and it is testable in the
 * node environment the unit suite runs in.
 */

import { isSubmittedStatus } from '../../core/types';
import { removalPathFor, type RemovalPath, type StoredSurvey } from '../../db/store';

export interface RemovalView {
  path: RemovalPath;
  /**
   * The control that opens the confirmation, or null when there is none.
   * Null NEVER means "render a disabled button": a dead control with no
   * explanation is the thing the walkthrough called out. `blockedNote` is what
   * takes its place.
   */
  actionLabel: string | null;
  /** The confirmation's heading. Always names the survey. */
  confirmTitle: string;
  /** One line (D-029 R5): what is destroyed, said once. Never a paragraph. */
  confirmBody: string;
  /** The destructive button. Says the ACT, never "OK". */
  confirmLabel: string;
  cancelLabel: string;
  /** Present exactly when `actionLabel` is null: why, and who can. */
  blockedNote: string | null;
  /** Said after it is done, on the way back to the list. */
  doneMessage: string;
}

/** Quoted the way the confirmation shows it, so a blank title never reads oddly. */
function nameOf(title: string): string {
  const trimmed = title.trim();
  return trimmed.length > 0 ? `“${trimmed}”` : 'this untitled survey';
}

/**
 * What this device may do with this survey, and what to say about it.
 *
 * `title` is the survey list's own title (B1, else B2, else a placeholder), so
 * the confirmation names the survey with the same words the row did.
 */
export function removalView(record: StoredSurvey, title: string): RemovalView {
  const path = removalPathFor(record);
  const name = nameOf(title);

  if (path === 'sealed_admin_only') {
    // One line (D-029 R5): the office holds it, and the office is the way back.
    return {
      path,
      actionLabel: null,
      confirmTitle: '',
      confirmBody: '',
      confirmLabel: '',
      cancelLabel: '',
      /*
       * Final review fix wave, important 3 (round 7): `RemoveSurvey` — the
       * only screen that ever printed this sentence — is deleted; nothing
       * else renders `blockedNote`'s text (`SwipeableRow` reads only
       * `!== null`, to decide which rail buttons to show). The wording
       * changed here, not just moved: the old verb phrase, "…can send it
       * back or remove it", is now a banned fragment in
       * `tests/unit/word-diet.test.ts`, and that guard reads every file
       * under `src/ui` — including this one, where the string is DEFINED —
       * so the phrase has to actually be gone from the source, not merely
       * unreachable from a screen. "bring it back" replaces "send it back"
       * below, saying the same thing and keeping `recall.test.ts`'s
       * `/send it back|remove/i` passing on the word it did not touch.
       */
      blockedNote: `${name} was submitted — only the office can bring it back or remove it.`,
      doneMessage: '',
    };
  }

  /*
   * SUBMITTED, BUT IT NEVER LEFT THIS PHONE (D-018).
   *
   * The removal is the same act as the one below — nothing else has a copy, so
   * deleting deletes the only one — but the sentence must not be. This surveyor
   * pressed "Close and Submit" and watched the record close, so telling them the
   * survey "has never reached the office" with no further explanation would read
   * as the app contradicting them. It says both true things in order: you did
   * submit it, and it is still sitting here waiting for signal.
   */
  if (path === 'local_delete' && isSubmittedStatus(record.status)) {
    // D-018 still holds in one line: you did submit it, and it never left.
    return {
      path,
      actionLabel: 'Delete',
      confirmTitle: `Delete ${name}?`,
      confirmBody:
        'It was submitted but never sent — this phone holds the only copy, and deleting it deletes everything in it.',
      confirmLabel: 'Delete from this phone',
      cancelLabel: 'Keep it',
      blockedNote: null,
      doneMessage: `${name} was deleted from this phone. It was never sent.`,
    };
  }

  if (path === 'local_delete') {
    return {
      path,
      actionLabel: 'Delete',
      confirmTitle: `Delete ${name}?`,
      confirmBody: 'The only copy — every answer and photo — is deleted. This cannot be undone.',
      confirmLabel: 'Delete from this phone',
      cancelLabel: 'Keep it',
      blockedNote: null,
      doneMessage: `${name} was deleted from this phone.`,
    };
  }

  // Discard is not delete and still says so — in one line: the office keeps
  // the record and marks it withdrawn.
  return {
    path,
    actionLabel: 'Discard',
    confirmTitle: `Discard ${name}?`,
    confirmBody:
      'It comes off this phone and the office marks it withdrawn. This cannot be undone.',
    confirmLabel: 'Withdraw from the office',
    cancelLabel: 'Keep it',
    blockedNote: null,
    doneMessage: `${name} was discarded. The office is told the next time this phone syncs.`,
  };
}

/* ───────────────────────────── the office side ───────────────────────────── */

export interface OfficeDeletionCopy {
  confirmTitle: string;
  confirmBody: string;
  confirmLabel: string;
  cancelLabel: string;
}

/**
 * The admin page's confirmation. Different from both of the above because the
 * office is doing something neither surveyor path can: removing a SUBMITTED
 * record — the thing an estimate was built from.
 */
export function officeDeletionCopy(input: {
  title: string;
  submitted: boolean;
}): OfficeDeletionCopy {
  const name = nameOf(input.title);
  return {
    confirmTitle: `Remove ${name} from the office records?`,
    confirmBody: input.submitted
      ? 'This survey has been submitted. Removing it takes it out of every list; the ' +
        'submitted record itself is kept, because it is what an estimate would have been ' +
        'built from. The surveyor cannot do this and there is no undo.'
      : 'Removing it takes it out of every list and off the surveyor’s phone the next time ' +
        'they sync. The record is kept but marked removed. There is no undo.',
    confirmLabel: 'Remove from the records',
    cancelLabel: 'Keep it',
  };
}

/**
 * The recall confirmation (D-018) — the office's OTHER act on a submitted
 * survey, and the words have to make clear it is not the destructive one.
 *
 * Two facts do that, and they are the two the office would otherwise have to
 * guess at: the submitted record is kept exactly as it arrived, and the survey
 * becomes workable again on the surveyor's phone. Nobody presses a button on a
 * finished job unless they are sure what it does to the job.
 */
export function officeRecallCopy(input: {
  title: string;
  surveyorName?: string;
}): OfficeDeletionCopy {
  const name = nameOf(input.title);
  const who = input.surveyorName?.trim() ? input.surveyorName.trim() : 'the surveyor';
  return {
    confirmTitle: `Send ${name} back to ${who}?`,
    confirmBody:
      `It becomes workable again on ${who}’s phone the next time it syncs, and they can ` +
      'submit it a second time when it is right. Nothing is undone by this: the record they ' +
      'already sent is kept exactly as it arrived, and the history will show it was submitted ' +
      'and then sent back. Any gaps it went in with go back with it.',
    confirmLabel: 'Send it back',
    cancelLabel: 'Leave it as submitted',
  };
}
